/**
 * @name: index.jsx
 * @description: 标记重做 - 数据表（复刻 destroy 数据明细，表格与筛选项一致）
 */
import React, { useState, useRef } from 'react';
import { Table } from '@components/table';
import { Space, message } from 'antd';
import { getRemakeLog, sessionAPI, taskFlowAPI } from '@api';
import { Button, LazyLoadingImage, TaskDrawer } from '@components';
import PreviewImage from '@components/image/preview_image';
import { FilterForm } from '@views/4_stats/remake/filterForm';
import ConfirmRedoModal from '@views/4_stats/destroy/ConfirmRedoModal';
import { momentArrDataToTimes } from '@views/4_stats/remake/utils';
import { useHistory } from 'react-router';
import { setRemakeMmark } from '@api';
import { $success, $error } from "@/components";

function request(page, pageSize, params) {
    const {
        create_time,
        discard_time,
        _refresh,
        ...other
    } = params || {};
    return getRemakeLog({
        page,
        page_size: pageSize,
        ...momentArrDataToTimes(create_time, 'create_time'),
        ...momentArrDataToTimes(discard_time, 'discard_time'),
        ...other
    })
        .then((res) => {
            // 拦截器已返回 response.data，res 即接口 body：{ code, data: { list, total } } 或 { list, total }
            const payload = res?.data ?? res;
            const list = Array.isArray(payload?.list)
                ? payload.list
                : Array.isArray(payload?.data?.list)
                ? payload.data.list
                : [];
            const total = typeof payload?.total === 'number'
                ? payload.total
                : typeof payload?.data?.total === 'number'
                ? payload.data.total
                : 0;
            return { list, total };
        })
        .catch(() => {
            return { list: [], total: 0 };
        });
}

const DestroyStatisticsRedo = function () {
    const history = useHistory();
    const [redoModalVisible, setRedoModalVisible] = useState(false);
    const [redoRecord, setRedoRecord] = useState(null);
    const [tableRefreshKey, setTableRefreshKey] = useState(0);
    const tableRef = useRef(null);
    const filterFormRef = useRef(null);

    const openRedoConfirm = (record) => {
        setRedoRecord(record);
        setRedoModalVisible(true);
    };

    const closeRedoConfirm = () => {
        setRedoModalVisible(false);
        setRedoRecord(null);
    };

    const handleRedoConfirm = async () => {
        if (!redoRecord?.task_id) return;
        try {
            setRemakeMmark({
                id: redoRecord.task_id,
                remake_mark: false
            }).then((res) => {
                setRedoModalVisible(false);
                setRedoRecord(null);
                $success('操作成功');
                setTableRefreshKey((k) => k + 1);
                setTimeout(() => filterFormRef.current?.submit?.(), 0);
            }).catch((err) => {
                setRedoModalVisible(false)
                setTimeout(() => filterFormRef.current?.submit?.(), 0);
            });
        } catch (e) {
            $error(e?.message)
        }
    };

    const columns = [
        {
            title: '预览图',
            dataIndex: 'review_image',
            render(text, record) {
                const src = record.review_image;
                const onPreview = function (src) {
                    PreviewImage.open({
                        images: [src],
                        request: (url) => sessionAPI.fetchOriginalImage(url)
                            .then(res => res.data?.origin_urls?.[0] || '')
                    });
                };
                return (
                    <LazyLoadingImage
                        preview={{ visible: false }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onPreview(src);
                        }}
                        width="60px"
                        height="60px"
                        src={src}
                    />
                );
            }
        },
        { title: '资产ID', dataIndex: 'asset_uid' },
        { title: '资产名称', dataIndex: 'asset_name' },
        { title: '创建时间', dataIndex: 'create_time' },
        { title: '作废人', dataIndex: 'operator_name' },
        { title: '作废类型', dataIndex: 'discard_type' },
        { title: '作废时间', dataIndex: 'discard_time' },
        {
            title: '项目来源',
            ellipsis: { showTitle: true },
            dataIndex: 'project_name'
        },
        { title: '备注', dataIndex: 'comment', ellipsis: true },
        {
            title: '操作',
            render(text, record) {
                return (
                    <Space>
                        <Button
                            type="link"
                            onClick={() => {
                                history.push(`/stats/destroy/redo?task_flow?taskId=${record.task_id}`);
                            }}
                        >
                            查看
                        </Button>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => openRedoConfirm(record)}
                        >
                            取消重做标记
                        </Button>
                    </Space>
                );
            }
        }
    ];

    return (
        <div
            className="page-container"
            style={{
                position: 'relative',
                height: 'calc(100VH - 66px)',
                overflow: 'hidden'
            }}
        >
            <div style={{ height: '100%', overflow: 'auto' }}>
                <Table
                    ref={tableRef}
                    filterFormRef={filterFormRef}
                    params={{ _refresh: tableRefreshKey }}
                    renderFilterForm={(props) => <FilterForm {...props} ref={props.filterFormRef} />}
                    url="/api/task-statistic/remake-log"
                    request={request}
                    columns={columns}
                />
            </div>
            <TaskDrawer />
            <ConfirmRedoModal
                visible={redoModalVisible}
                onCancel={closeRedoConfirm}
                onConfirm={handleRedoConfirm}
                content="是否取消标记为重做状态"
            />
        </div>
    );
};

export default DestroyStatisticsRedo;
